import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, forwardRef, input } from "@angular/core";
import { InputFormWrapper} from "../wrapper.interface";
import { MultiSelectConfig } from "./multiSelect.config";
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { GroupedOption, Option } from "src/util/types/global";
import { PrimeTemplate, SharedModule } from "primeng/api";
import { MultiSelect, MultiSelectModule } from "primeng/multiselect";
import { V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { TranslateModule } from "@ngx-translate/core";


@Component({
    selector:      'v-multiSelect',
    templateUrl:   './multiSelect.component.haml',
    styleUrls:     ['./multiSelect.component.sass', '../input.style.sass'],
    encapsulation: ViewEncapsulation.None,
    providers:     [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VMultiSelectComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VMultiSelectComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VMultiSelectComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, MultiSelectModule, ReactiveFormsModule, SharedModule, TranslateModule]
})
export class VMultiSelectComponent<T = unknown, TValue = string[]>  extends InputFormWrapper<MultiSelectConfig, TValue> {
    protected readonly _baseConfig: MultiSelectConfig = MultiSelectConfig.baseTheme.Default;
    options = input<(Option<T> | GroupedOption<T>)[]>();
    filterBy = input<string>();

    // TODO: discuss opinionated label/value/filter. We can either assume we want to display data from value or require the developer to be specific.
    cLabel = computed(() => {
        const label = this.config().optionLabel;
        if (!label || typeof this.options().find(x => x.value)?.value !== "object") return label ?? 'label';
        return `value.${label}`;
    });
    cValue = computed(() => {
        const value = this.config().optionValue;
        if (!value || typeof this.options().find(x => x.value)?.value !== "object") return value ?? 'value';
        return `value.${value}`;
    });

    cFilter = computed(() => {
        let filter = this.filterBy();
        if (filter === "") filter = "label"; // Default filter by label if explicit filter is provided, but attribute is set
        const obj = this.options().find(x => x.value)?.value as object;
        if (!filter || (obj && !(typeof obj == 'object' && filter in obj))) return filter;
        return `value.${filter}`;
    });

    cGroup = computed(() => {
        const group = this.config().optionGroup ?? 'items';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const obj = this.options().find(x => group in x && Array.isArray((x as any)[group]));
        return !!obj;
    });

    ngAfterViewInit() { // Prime Update 19 reassigns templates to undefined after initialization
      this.gcTemplateRef().forEach(x => this.assignTemplate(x));
      this.primeTemplateRef().forEach(x => this.assignTemplate(x));
    }

    protected assignTemplate(x: PrimeTemplate) {
        const el = this.wrappedComponent as MultiSelect;
        switch(x.name) {
            case 'header':
                el.headerTemplate = x.template;
                break;
            case 'footer':
                el.footerTemplate = x.template;
                break;
            case 'filter':
                el.filterTemplate = x.template;
                break;
            case 'item':
                el.itemTemplate = x.template;
                break;
            case 'selectedItems':
                el.selectedItemsTemplate = x.template;
                break;
        }
    }
}